import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Card } from "../../../../../components/ui/card";
import { Button } from "../../../../../components/ui/button";
import { useWorkStepImport } from "../../../../../contexts/workStepImportContext";
import { validateFileSizeWithToast } from "../../../../../lib/fileUploadValidation";

// Work Step spreadsheet dropzone + Continue step. Continue navigates into
// column mapping; the parsed file travels via WorkStepImportProvider.
export const WorkStepsUploadCard = ({
  onCancel,
}: {
  onCancel?: () => void;
}) => {
  const navigate = useNavigate();
  const { setFileAndParse, parsed, fileName, mapping, loading, clear } =
    useWorkStepImport();

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles || acceptedFiles.length === 0) return;
      const file = acceptedFiles[0];

      if (!validateFileSizeWithToast(file)) return;

      await setFileAndParse(file);
    },
    [setFileAndParse],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
    multiple: false,
  });

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    clear();
  };

  return (
    <Card className="p-6">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg min-h-[220px] flex flex-col items-center justify-center text-center p-6 cursor-pointer transition ${
          isDragActive ? "bg-stone-50 border-stone-400" : "border-stone-300"
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="h-10 w-10 text-stone-400 mb-3" />
        <div className="font-medium mb-1 flex items-center gap-2">
          {fileName ? (
            <>
              <span>{fileName}</span>
              <button
                onClick={handleRemoveFile}
                className="p-1 hover:bg-stone-200 rounded-full transition"
                aria-label="Remove file"
              >
                <X className="h-4 w-4 text-stone-600" />
              </button>
            </>
          ) : (
            "Drag and drop your file here, or click to browse"
          )}
        </div>
        <div className="text-xs text-stone-500">
          We'll analyze your document and auto-detect common columns.
        </div>
      </div>
      <div className="bg-stone-50 border rounded mt-4 p-3 text-xs text-stone-700">
        What happens next? We'll read your document automatically — Work Step
        Name, Description, Section, Preparer, and Audit — and show you a review
        grouped by audit before anything is imported.
      </div>
      <div className="flex justify-end gap-2 mt-4">
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          onClick={() => {
            // Mapping is hardcoded from product knowledge of work step
            // exports; the only unrecoverable miss is the name column.
            if (!mapping.name) {
              toast.error(
                'Couldn\'t find a work step name column. Expected a header like "Title" or "Work Step Name".',
              );
              return;
            }
            navigate("/audit/work-steps/import/review");
          }}
          disabled={!parsed || loading}
          loading={loading}
        >
          Continue
        </Button>
      </div>
    </Card>
  );
};
